import { Badge } from '@/ui/Badge';
import { Skeleton } from '@/ui/Skeleton';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { useMatriz, type CoberturaDeCompetencia } from './api';

/**
 * Matriz — para cada cargo con requisitos, qué parte de su gente tiene ya cada
 * competencia.
 *
 * ## Un cargo que nadie tiene salía como una brecha total
 *
 * Un requisito puede nombrar un cargo que ningún perfil lleva escrito —en la
 * base del 2026-09-22, el único cargo con requisitos es así—. El vanilla lo
 * pintaba «0/0 · 0 %» en rojo, igual que un cargo entero sin formar. No es una
 * brecha: no hay a quién medir. Ahora lo dice, y sugiere lo más probable, que el
 * cargo esté escrito distinto en el requisito y en los perfiles.
 *
 * ## El estado, con su palabra
 *
 * Verde, naranja y rojo iban solo en el color de la barra y del porcentaje. Ahora
 * cada competencia lleva «Cubierta», «A medias» o «Brecha».
 */

function estado(c: CoberturaDeCompetencia) {
  if (c.pct >= 80) return { tono: 'ok' as const, texto: 'Cubierta' };
  if (c.pct >= 50) return { tono: 'warn' as const, texto: 'A medias' };
  return { tono: 'danger' as const, texto: 'Brecha' };
}

export function Matriz({ onIrAGestion }: { onIrAGestion: () => void }) {
  const matriz = useMatriz(true);

  if (matriz.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-36 w-full" />
      </div>
    );
  }
  if (matriz.isError || !matriz.data) {
    return <EstadoError err={matriz.error} que="la matriz" onReintentar={() => void matriz.refetch()} />;
  }
  if (!matriz.data.length) {
    return (
      <EstadoVacio
        icono="bi-grid-3x3-gap"
        titulo="Sin matriz definida"
        descripcion="Crea competencias en «Gestión» y pon el cargo que las requiere para ver aquí las brechas."
        accion={{ texto: 'Ir a Gestión', onClick: onIrAGestion }}
      />
    );
  }

  return (
    <ul className="flex flex-col gap-4">
      {matriz.data.map((cargo) => (
        <li key={cargo.position} className="rounded-md border border-line bg-surface p-4">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="flex items-center gap-1.5 text-3 font-bold text-brand-ink">
              <i className="bi bi-briefcase" aria-hidden="true" /> {cargo.position}
            </h3>
            <span className="text-2 text-muted">
              {cargo.user_count
                ? `${cargo.user_count} ${cargo.user_count === 1 ? 'persona' : 'personas'}`
                : 'Nadie tiene este cargo'}
            </span>
          </div>

          {!cargo.user_count ? (
            <p className="flex items-start gap-2 rounded-sm border border-line bg-sunken p-2.5 text-2 text-ink-2">
              <i className="bi bi-info-circle mt-0.5 text-brand-ink" aria-hidden="true" />
              Ningún perfil activo tiene este cargo, así que no hay cobertura que medir. Lo más probable
              es que el cargo esté escrito de otra forma en los perfiles.{' '}
              {cargo.competencies.length} {cargo.competencies.length === 1 ? 'competencia lo requiere' : 'competencias lo requieren'}.
            </p>
          ) : !cargo.competencies.length ? (
            <p className="text-2 text-muted">Sin competencias asignadas a este cargo.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {cargo.competencies.map((c) => {
                const e = estado(c);
                return (
                  <li key={c.id}>
                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-2">
                      <span className="flex items-center gap-2 text-ink">
                        {c.name} <Badge tone={e.tono}>{e.texto}</Badge>
                      </span>
                      <strong className="tabular-nums text-ink-2">
                        {c.covered}/{c.total} · {Math.round(c.pct)} %
                      </strong>
                    </div>
                    <BarraDeProgreso pct={c.pct} etiqueta={`Cobertura de ${c.name} en ${cargo.position}`} />
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}
